/**
 * DeliveryTimeline - Timeline visual de entregas
 * Componente premium para exibicao de paradas de rota
 *
 * Features:
 * - Timeline vertical conectada com linha
 * - Status visual (pendente, em andamento, concluido, pulado)
 * - Cores por prioridade
 * - Horarios estimados e reais
 * - Tempo de servico
 * - Suporte a clique e selecao
 */

import { memo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as PendingIcon,
  DirectionsCar as EnRouteIcon,
  LocationOn as ArrivedIcon,
  Cancel as SkippedIcon,
  Error as FailedIcon,
  Flag as StartIcon,
  FlagOutlined as EndIcon,
  Schedule as TimeIcon,
  Timer as ServiceTimeIcon,
  PriorityHigh as HighPriorityIcon,
  KeyboardArrowUp as CriticalIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { formatArrivalTime, formatDuration } from '../../common/services/routingService';

// Status de parada
const STOP_STATUS = {
  PENDING: 'pending',
  EN_ROUTE: 'en_route',
  ARRIVED: 'arrived',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
  FAILED: 'failed',
};

// Prioridades
const PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

// Configuracao de status
const getStatusConfig = (status, tokens) => {
  switch (status) {
    case STOP_STATUS.COMPLETED:
      return {
        icon: CompletedIcon,
        color: tokens?.semantic?.success || '#22c55e',
        bgColor: `${tokens?.semantic?.success || '#22c55e'}20`,
        label: 'Concluido',
      };
    case STOP_STATUS.EN_ROUTE:
      return {
        icon: EnRouteIcon,
        color: tokens?.accent?.primary || '#00d4aa',
        bgColor: `${tokens?.accent?.primary || '#00d4aa'}20`,
        label: 'A caminho',
        pulse: true,
      };
    case STOP_STATUS.ARRIVED:
      return {
        icon: ArrivedIcon,
        color: tokens?.semantic?.info || '#3b82f6',
        bgColor: `${tokens?.semantic?.info || '#3b82f6'}20`,
        label: 'No local',
        pulse: true,
      };
    case STOP_STATUS.SKIPPED:
      return {
        icon: SkippedIcon,
        color: tokens?.semantic?.warning || '#f59e0b',
        bgColor: `${tokens?.semantic?.warning || '#f59e0b'}20`,
        label: 'Pulado',
      };
    case STOP_STATUS.FAILED:
      return {
        icon: FailedIcon,
        color: tokens?.semantic?.error || '#ef4444',
        bgColor: `${tokens?.semantic?.error || '#ef4444'}20`,
        label: 'Falhou',
      };
    default: // PENDING
      return {
        icon: PendingIcon,
        color: tokens?.text?.tertiary || '#6e7681',
        bgColor: tokens?.surface?.elevated || '#161b22',
        label: 'Pendente',
      };
  }
};

// Configuracao de prioridade
const getPriorityConfig = (priority, tokens) => {
  switch (priority) {
    case PRIORITY.CRITICAL:
      return {
        icon: CriticalIcon,
        color: tokens?.semantic?.error || '#ef4444',
        label: 'Critico',
        showBadge: true,
      };
    case PRIORITY.HIGH:
      return {
        icon: HighPriorityIcon,
        color: tokens?.semantic?.warning || '#f59e0b',
        label: 'Alta',
        showBadge: true,
      };
    case PRIORITY.LOW:
      return {
        color: tokens?.text?.tertiary || '#6e7681',
        label: 'Baixa',
        showBadge: false,
      };
    default: // MEDIUM
      return {
        color: tokens?.text?.secondary || '#8b949e',
        label: 'Media',
        showBadge: false,
      };
  }
};

// Componente de item da timeline
const TimelineItem = memo(({
  step,
  index,
  isFirst,
  isLast,
  isActive,
  routeColor,
  showConnector,
  onClick,
  tokens,
}) => {
  const statusConfig = getStatusConfig(step.status, tokens);
  const priorityConfig = getPriorityConfig(step.priority, tokens);
  const StatusIcon = statusConfig.icon;

  const isStartOrEnd = step.type === 'start' || step.type === 'end';

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        minHeight: isStartOrEnd ? 48 : 72,
      }}
    >
      {/* Coluna da timeline (indicador + linha) */}
      <Box
        sx={{
          width: 48,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {/* Linha conectora superior */}
        {!isFirst && showConnector && (
          <Box
            sx={{
              width: 2,
              height: 12,
              backgroundColor: step.status === STOP_STATUS.COMPLETED
                ? statusConfig.color
                : tokens?.border?.subtle,
            }}
          />
        )}

        {/* Indicador circular */}
        <Box
          sx={{
            width: isStartOrEnd ? 28 : 32,
            height: isStartOrEnd ? 28 : 32,
            borderRadius: '50%',
            backgroundColor: isStartOrEnd
              ? (step.type === 'start' ? tokens?.semantic?.success : tokens?.text?.tertiary)
              : statusConfig.bgColor,
            border: `2px solid ${isStartOrEnd
              ? (step.type === 'start' ? tokens?.semantic?.success : tokens?.text?.tertiary)
              : statusConfig.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.2s ease',
            // Efeito pulse para status ativos
            ...(statusConfig.pulse && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { boxShadow: `0 0 0 0 ${statusConfig.color}40` },
                '70%': { boxShadow: `0 0 0 8px ${statusConfig.color}00` },
                '100%': { boxShadow: `0 0 0 0 ${statusConfig.color}00` },
              },
            }),
          }}
        >
          {isStartOrEnd ? (
            step.type === 'start' ? (
              <StartIcon sx={{ color: '#fff', fontSize: 14 }} />
            ) : (
              <EndIcon sx={{ color: '#fff', fontSize: 14 }} />
            )
          ) : step.status === STOP_STATUS.PENDING ? (
            <Typography
              sx={{
                color: routeColor || statusConfig.color,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {index}
            </Typography>
          ) : (
            <StatusIcon sx={{ color: statusConfig.color, fontSize: 16 }} />
          )}
        </Box>

        {/* Linha conectora inferior */}
        {!isLast && showConnector && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 20,
              backgroundColor: step.status === STOP_STATUS.COMPLETED
                ? statusConfig.color
                : tokens?.border?.subtle,
            }}
          />
        )}
      </Box>

      {/* Conteudo do item */}
      <Box
        onClick={() => onClick?.(step)}
        sx={{
          flex: 1,
          py: 1,
          pl: 1.5,
          pr: 1,
          cursor: onClick ? 'pointer' : 'default',
          borderRadius: tokens?.radius?.md,
          backgroundColor: isActive ? `${routeColor || statusConfig.color}10` : 'transparent',
          border: isActive ? `1px solid ${routeColor || statusConfig.color}` : '1px solid transparent',
          transition: 'all 0.15s ease',
          '&:hover': onClick ? {
            backgroundColor: tokens?.surface?.hover,
          } : {},
        }}
      >
        {isStartOrEnd ? (
          // Start/End simples
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
              {step.type === 'start' ? 'Inicio da rota' : 'Fim da rota'}
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
              {formatArrivalTime(step.arrival || 0)}
            </Typography>
          </Box>
        ) : (
          // Parada de entrega
          <>
            {/* Linha superior: Nome + Prioridade + Horario */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    sx={{
                      color: tokens?.text?.primary,
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {step.name || `Entrega #${step.id}`}
                  </Typography>

                  {/* Badge de prioridade alta */}
                  {priorityConfig.showBadge && (
                    <Tooltip title={`Prioridade ${priorityConfig.label}`}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: `${priorityConfig.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {priorityConfig.icon && (
                          <priorityConfig.icon sx={{ color: priorityConfig.color, fontSize: 12 }} />
                        )}
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* Endereco */}
                {step.address && (
                  <Typography
                    sx={{
                      color: tokens?.text?.tertiary,
                      fontSize: '11px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mt: 0.25,
                    }}
                  >
                    {step.address}
                  </Typography>
                )}
              </Box>

              {/* Horarios */}
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                  <TimeIcon sx={{ fontSize: 12, color: tokens?.text?.tertiary }} />
                  <Typography
                    sx={{
                      color: tokens?.text?.primary,
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {formatArrivalTime(step.arrival || 0)}
                  </Typography>
                </Box>

                {/* Horario real vs estimado */}
                {step.actualArrival && (
                  <Typography
                    sx={{
                      color: step.actualArrival > step.arrival
                        ? tokens?.semantic?.error
                        : tokens?.semantic?.success,
                      fontSize: '10px',
                    }}
                  >
                    Real: {formatArrivalTime(step.actualArrival)}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Linha inferior: Status + Tempo de servico + Chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {/* Chip de status */}
              <Chip
                label={statusConfig.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '10px',
                  fontWeight: 500,
                  backgroundColor: statusConfig.bgColor,
                  color: statusConfig.color,
                  '& .MuiChip-label': { px: 1 },
                }}
              />

              {/* Tempo de servico */}
              {step.serviceTime > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <ServiceTimeIcon sx={{ fontSize: 11, color: tokens?.text?.tertiary }} />
                  <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '10px' }}>
                    {Math.round(step.serviceTime / 60)} min
                  </Typography>
                </Box>
              )}

              {/* Tempo de espera */}
              {step.waitingTime > 0 && (
                <Typography sx={{ color: tokens?.semantic?.warning, fontSize: '10px' }}>
                  +{Math.round(step.waitingTime / 60)} min espera
                </Typography>
              )}

              {/* Janela de tempo */}
              {step.timeWindowStart && step.timeWindowEnd && (
                <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '10px' }}>
                  {step.timeWindowStart}-{step.timeWindowEnd}
                </Typography>
              )}
            </Box>

            {/* Notas de conclusao */}
            {step.completionNotes && (
              <Typography
                sx={{
                  color: tokens?.text?.tertiary,
                  fontSize: '10px',
                  fontStyle: 'italic',
                  mt: 0.5,
                }}
              >
                "{step.completionNotes}"
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
});

TimelineItem.displayName = 'TimelineItem';

// Componente principal da timeline
const DeliveryTimeline = memo(({
  steps = [],
  routeColor,
  activeStepId,
  onStepClick,
  showConnector = true,
  _compact = false,
  vehicleName,
  totalDistance,
  totalDuration,
}) => {
  const tokens = usePremiumTokens();

  // Filtrar e ordenar steps
  const sortedSteps = [...steps].sort((a, b) => {
    // Start primeiro, End ultimo
    if (a.type === 'start') return -1;
    if (b.type === 'start') return 1;
    if (a.type === 'end') return 1;
    if (b.type === 'end') return -1;
    // Jobs por ordem otimizada ou arrival
    return (a.optimizedOrder || a.arrival || 0) - (b.optimizedOrder || b.arrival || 0);
  });

  // Contar estatisticas
  const jobSteps = sortedSteps.filter((s) => s.type === 'job');
  const completedCount = jobSteps.filter((s) => s.status === STOP_STATUS.COMPLETED).length;
  const skippedCount = jobSteps.filter((s) => s.status === STOP_STATUS.SKIPPED).length;
  const pendingCount = jobSteps.length - completedCount - skippedCount;

  // Calcular indice visual (apenas jobs, excluindo start/end)
  let jobIndex = 0;

  return (
    <Box>
      {/* Header com resumo */}
      {(vehicleName || totalDistance || totalDuration) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            pb: 1.5,
            borderBottom: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          {vehicleName && (
            <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '14px' }}>
              {vehicleName}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Contadores de status */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={`${completedCount} concluidas`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: `${tokens?.semantic?.success}20`,
                  color: tokens?.semantic?.success,
                }}
              />
              {pendingCount > 0 && (
                <Chip
                  label={`${pendingCount} pendentes`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '10px',
                    backgroundColor: tokens?.surface?.elevated,
                    color: tokens?.text?.secondary,
                  }}
                />
              )}
              {skippedCount > 0 && (
                <Chip
                  label={`${skippedCount} puladas`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '10px',
                    backgroundColor: `${tokens?.semantic?.warning}20`,
                    color: tokens?.semantic?.warning,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Timeline */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {sortedSteps.map((step, index) => {
          const isJob = step.type === 'job';
          if (isJob) jobIndex += 1;

          return (
            <TimelineItem
              key={step.id || `${step.type}-${index}`}
              step={step}
              index={isJob ? jobIndex : null}
              isFirst={index === 0}
              isLast={index === sortedSteps.length - 1}
              isActive={activeStepId === step.id}
              routeColor={routeColor}
              showConnector={showConnector}
              onClick={onStepClick}
              tokens={tokens}
            />
          );
        })}
      </Box>

      {/* Footer com totais */}
      {(totalDistance || totalDuration) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
            pt: 1.5,
            borderTop: `1px solid ${tokens?.border?.subtle}`,
          }}
        >
          {totalDistance && (
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
              Total: {typeof totalDistance === 'number'
                ? `${(totalDistance / 1000).toFixed(1)} km`
                : totalDistance}
            </Typography>
          )}
          {totalDuration && (
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>
              {typeof totalDuration === 'number'
                ? formatDuration(totalDuration)
                : totalDuration}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
});

DeliveryTimeline.displayName = 'DeliveryTimeline';

export default DeliveryTimeline;

// Export adicional para uso em outros componentes
export {
 STOP_STATUS, PRIORITY, getStatusConfig, getPriorityConfig 
};
